import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getAccessToken } from "../../services/authApi";
import { fieldReader, getTpoOrganizationById, getConditions, fetchLoanConditions } from "../../services/loanApi";
import { info, error as logAppInsightError, warn as logAppInsightWarn } from '../../utils/logger';
import { logError } from "../../utils/logError";
import '../menu/menu.css';
import {
    ChevronDown, ChevronRight, CreditCard, Check
} from 'lucide-react';
import { menuItemsCP, menuItemsBP, menuItemsDelegate, menuItemsNonDelegate, menuItemsDelegateBP, menuItemsNonDelegateBP, evaluateMenuCondition, loanFieldIdsToRead, isCreditMenuCompleted } from '../menu/menuItems';
import { checkValidationRequired } from '../../utils/validationRedirect';
import { markGuidelineExceptionValidationRedirect } from '../../utils/guidelineExceptionValidationRoute';
import SidebarMenuFacebookLoader from './SidebarMenuFacebookLoader';
import elliemaeApi from '../../services/interceptors/elliemaeApi';
import { hasPermission } from '../../utils/authUtils';
import { getLoggedInUserPersonas } from '../../utils/userPersonas';
import { getServiceOrders } from '../../services/orderService';

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const isConditionBetaEnabled = process.env.REACT_APP_ENABLE_CONDITIONBETA_PAGE === 'true';

const resolveAiDocReviewApiBaseUrl = () => {
    if (API_DOMAIN) {
        return `${API_DOMAIN}/api/recommended-doc-review`;
    }

    return "/api/recommended-doc-review";
};

const SidebarMenu = () => {
    const [loading, setLoading] = useState(true);
    const [loanTypeValue, setLoanTypeValue] = useState('');
    const [loanOccupancy, setLoanOccupancy] = useState('');
    const [loanOfficerNMLSID, setLoanOfficerNMLSID] = useState('');
    const [loanID, setLoanID] = useState('');
    const [expandedSections, setExpandedSections] = useState({
        'URLA 2020': false
    });
    const [activeMenu, setActiveMenu] = useState('');
    const [loanFields, setLoanFields] = useState({});
    const [countVisibility, setCountVisibility] = useState({});
    const [errorMessage, setErrorMessage] = useState('');
    const [showDisclosures, setShowDisclosures] = useState(false);
    const [showSendDisclosures, setShowSendDisclosures] = useState(false);
    const [showRequestLE, setShowRequestLE] = useState(false);
    const [brokerOptions, setbrokerOptions] = useState('');
    const [conditionsCount, setConditions] = useState([]);
    const [allConditions, setAllConditions] = useState([]);
    const [loanSubmit, setLoanSubmit] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const [serviceOrders, setServiceOrders] = useState([]);
    const [currentLoanNumber, setCurrentLoanNumber] = useState('');
    const [aiDocReviewPendingCount, setAiDocReviewPendingCount] = useState(null);
    const [aiDocReviewStatusLoaded, setAiDocReviewStatusLoaded] = useState(false);

    const [loanFileSequence, setLoanFileSequence] = useState(null);
    const tokenRef = useRef(null);
    const pollingRef = useRef(false);
    const regenLogOnceRef = useRef(false);
    const inactiveLoggedRef = useRef(false);
    const loanIdRef = useRef(null);
    const [documents, setDocuments] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [userPersonas, setUserPersonas] = useState([]);
    const disclosureLogsCache = {};
    const disclosureAllCacheRef = useRef({});

    const fetchAiDocReviewPendingCount = async (loanNumber) => {
        if (!loanNumber) {
            setAiDocReviewPendingCount(null);
            setAiDocReviewStatusLoaded(true);
            return;
        }

        setAiDocReviewStatusLoaded(false);

        try {
            const apiUrl = `${resolveAiDocReviewApiBaseUrl()}/loan/${encodeURIComponent(loanNumber)}`;
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            let payload = null;
            try {
                payload = await response.json();
            } catch {
                payload = null;
            }

            if (!response.ok) {
                throw new Error(payload?.message || "Failed to load AI Document Review status.");
            }

            const collection = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.documents)
                        ? payload.documents
                        : Array.isArray(payload?.items)
                            ? payload.items
                            : [];

            const pendingCount = collection.filter((item) => {
                const rawStatus = item?.userAcceptStatus ?? item?.UserAcceptStatus ?? "Pending";
                return String(rawStatus).trim().toLowerCase() === "pending";
            }).length;

            setAiDocReviewPendingCount(pendingCount);
        } catch (err) {
            console.error("[SidebarMenu] Failed to load AI Document Review pending count:", err);
            setAiDocReviewPendingCount(null);
        } finally {
            setAiDocReviewStatusLoaded(true);
        }
    };

    const incomeTypeToFolderMap = {
        "1099": ["IRS 1099"],
        "Asset Utilization": ["Assets"],
        "Bank Statement - Standard": ["Bank Statement"],
        "Bank Statement - Streamline": ["Bank Statement"],
        "Bank Statement - Standard 12 months": ["Bank Statement"],
        "Bank Statement - Standard 24 months": ["Bank Statement"],
        "P&L": ["Profit and Loss Statement"],
        "P&L + Bank Statements": ["Profit and Loss Statement", "Bank Statement"],
        "Tax Returns": ["Tax Returns"],
        "W2": ["Paystub", "IRS w-2"],
        "WVOE": ["Verification Of Employment"],
    };
    const correspondentDocumentMenuCompleteRules = [
        {
            condition: (loanFields) => {
                const loanType = loanFields["2626"]; // Loan Type
                return (
                    loanType?.toLowerCase() === "correspondent"
                );
            },
            folders: ["Income"]
        }
    ]


    const conditionToFolderRules = [
        {
            condition: (loanFields) => {
                const loanType = loanFields["19"]; // Loan Type
                const streetAddress = loanFields["URLA.X73"]; // Subject Property Street Address
                // Folder required only if loanType is 'Purchase' and streetAddress exists and is not 'TBD'
                return (
                    loanType === "Purchase" &&
                    streetAddress &&
                    streetAddress.trim().toUpperCase() !== "TBD"
                );
            },
            folders: ["Purchase Agreement"]
        },
        {
            condition: (loanFields) => loanFields["CX.LOAN.PROGRAM.IDENTIFIER"] !== "CFNINC",
            folders: ["Credit Report"]
        },
        {
            // Stock case
            condition: (loanFields) => {
                const incomeType = loanFields["CX.INCOME.TYPE"];
                if (incomeType !== "Other (retirement, etc)") return false;

                const ddKeys = ["DD0108", "DD0208", "DD0308", "DD0408", "DD0508", "DD0608", "DD0708", "DD0808", "DD0908", "DD1008"];
                return ddKeys.some(k => loanFields[k] === "Stock");
            },
            folders: ["Assets"]
        },
        {
            // Retirement Funds case
            condition: (loanFields) => {
                const incomeType = loanFields["CX.INCOME.TYPE"];
                if (incomeType !== "Other (retirement, etc)") return false;

                const ddKeys = ["DD0108", "DD0208", "DD0308", "DD0408", "DD0508", "DD0608", "DD0708", "DD0808", "DD0908", "DD1008"];
                return ddKeys.some(k => loanFields[k] === "Retirement Funds");
            },
            folders: ["Retirement Statement"]
        }
    ];

    useEffect(() => {
        const DIRTY_FLAG_KEY = "loan_additional_info_dirty";
        localStorage.removeItem(DIRTY_FLAG_KEY); // clear any stale unsaved-change flags
    }, []);


    useEffect(() => {
        const initSidebarMenu = async () => {
            if (window.elli && window.elli.script && window.elli.script.guest) {
                window.elli.script.guest.create();
                await loadLoanSummary();
            } else {
                console.warn("elli.script.guest is not available.");
            }
        };
        initSidebarMenu();
    }, []);

    // Load logged-in user's personas from the elli SDK auth object.
    // Used to gate correspondent menu visibility via `requiredPermission`.
    useEffect(() => {
        const loadUserPersonas = async () => {
            const personas = await getLoggedInUserPersonas();

            // Add mocking personas here for testing without elli auth context, e.g.:
            //personas.push("Corr - Full Access w/o Pricing"); // <-- example persona for testing
            //personas.push("Corr - Pipeline & Resources"); // <-- example persona for testing
            //personas.push("Corr - Full Access"); // <-- example persona for testing
            console.log("User personas loaded:", personas);
            setUserPersonas(personas);
        };
        loadUserPersonas();
    }, []);

    const loadLoanSummary = async () => {
        try {
            window.elli.script.getObject("tpoApplication").then(async function (tpoApplicationObject) {
                const tpoAppObject = tpoApplicationObject;
                let attempts = 0;
                const maxAttempts = 10;
                const delay = 200;

                async function tryGetLoanSummary() {
                    try {
                        const loanSummary = await tpoAppObject.getLoanSummary();

                        if (loanSummary && loanSummary.loanId) {
                            info("SideBar Menu loaded", {
                                loanid: loanSummary.loanId,
                                loanNumber: loanSummary.loanNumber || "N/A"
                            });
                            setLoanID(loanSummary.loanId);
                            setCurrentLoanNumber(loanSummary.loanNumber || "");
                            loanIdRef.current = loanSummary.loanId;
                            await loadData(loanSummary.loanId, loanSummary.loanNumber || "");
                            await getLoanConditions(loanSummary.loanId);
                            console.log("Setting Loan Id: " + loanSummary.loanId);
                        } else if (attempts < maxAttempts) {
                            attempts++;
                            console.log("tryGetLoanSummary- attempts: " + attempts);
                            setTimeout(tryGetLoanSummary, delay);
                        } else {
                            setErrorMessage("Unable to load loan information after multiple attempts.");
                            const errorCode = logError("SIDEBAR_TPO_ERROR");
                            logAppInsightWarn("Loan summary not available after max attempts", {
                                component: "SideBarMenu",
                                problemId: errorCode,
                                errorCode,
                                errorMessage: `${errorCode} - Failed to get loan summary after ${maxAttempts} attempts.`
                            });
                        }
                    } catch (error) {
                        console.error("Error in tryGetLoanSummary:", error);

                        if (attempts < maxAttempts) {
                            attempts++;
                            console.log("tryGetLoanSummary error - retry attempt: " + attempts);
                            setTimeout(tryGetLoanSummary, delay);
                        } else {
                            setErrorMessage("Unable to load loan information due to repeated errors.");
                            const errorCode = logError("SIDEBAR_TPO_ERROR");
                            logAppInsightWarn("Loan summary retrieval failed", {
                                component: "SideBarMenu",
                                problemId: errorCode,
                                errorCode,
                                rawError: error,
                                errorMessage: `${errorCode} - Failed to get loan summary after ${maxAttempts} attempts with error: ${error.message}`
                            });
                        }
                    }
                }

                // Start the initial attempt
                tryGetLoanSummary();
            });
        } catch (error) {
            console.error("Error initializing TPO Application Object:", error);
            setErrorMessage("An error occurred while loading the loan data.");
            const errorCode = logError("SIDEBAR_TPO_ERROR");
            logAppInsightError("Side Bar Menu Initialization Failed", {
                component: "SideBarMenu",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load Side Bar Menu Information.`
            });
        }
    };

    const regenerateToken = async () => {
        // Avoid regeneration while tab is hidden or device is offline
        if (document.hidden || !navigator.onLine) {
            throw new Error('ENVIRONMENT_INACTIVE');
        }
        const tpoAuthObject = await window.elli.script.getObject("auth");
        const authCode = await tpoAuthObject.createAuthCode();
        return await getAccessToken(authCode);
    };


    useEffect(() => {
        if (!loanFileSequence || !loanIdRef.current || pollingRef.current) return;
        pollingRef.current = true;

        const fetchLoanFileSequence = async () => {
            try {
                // Do not poll when tab is hidden or device is offline
                if (document.hidden || !navigator.onLine) {
                    if (!inactiveLoggedRef.current) {
                        console.info('Polling paused: environment inactive (hidden/offline).');
                        inactiveLoggedRef.current = true;
                    }
                    return;
                }
                // Environment active; clear inactive log latch
                inactiveLoggedRef.current = false;
                if (!tokenRef.current) {
                    tokenRef.current = await regenerateToken();
                    // successful regeneration; clear one-time log latch
                    regenLogOnceRef.current = false;
                }

                const response = await fieldReader(tokenRef.current, ["LOANFILESEQUENCENUMBER"], loanIdRef.current);
                const currentSequence = response["LOANFILESEQUENCENUMBER"];

                if (currentSequence !== loanFileSequence) {
                    console.info(`LOANFILESEQUENCENUMBER changed from sidebar: ${loanFileSequence} → ${currentSequence}`);
                    setLoanFileSequence(currentSequence);
                    loadData(loanID); // This will also update loanIdRef again
                }
            } catch (error) {
                const status = error?.response?.status || error?.status;
                if (status === 401) {
                    console.warn("Token expired. Regenerating...");
                    try {
                        // Clear stale token and try again
                        tokenRef.current = null;
                        // Skip regeneration when hidden/offline
                        if (document.hidden || !navigator.onLine) {
                            if (!inactiveLoggedRef.current) {
                                console.info('Token regeneration skipped: environment inactive (hidden/offline).');
                                inactiveLoggedRef.current = true;
                            }
                            return;
                        }
                        tokenRef.current = await regenerateToken();
                        // success; reset error-log latch
                        regenLogOnceRef.current = false;
                    } catch (tokenError) {
                        // Suppress repetitive logs while hidden/offline or when environment inactive
                        if (document.hidden || !navigator.onLine || tokenError?.message === 'ENVIRONMENT_INACTIVE') {
                            if (!inactiveLoggedRef.current) {
                                console.info('Token regeneration skipped: environment inactive (hidden/offline).');
                                inactiveLoggedRef.current = true;
                            }
                            return;
                        }
                        if (!regenLogOnceRef.current) {
                            const errorCode = logError("SIDEBAR_LOAD_ERROR");
                            logAppInsightWarn("Token Regeneration Error", {
                                component: "SideBarMenu",
                                problemId: errorCode,
                                errorCode,
                                rawError: tokenError,
                                errorMessage: `${errorCode} - Token regeneration failed during polling.`
                            });
                            regenLogOnceRef.current = true;
                        }
                    }
                } else {
                    // Suppress logs while hidden/offline; log once otherwise
                    if (document.hidden || !navigator.onLine) return;
                    if (!regenLogOnceRef.current) {
                        const errorCode = logError("SIDEBAR_LOAD_ERROR");
                        logAppInsightWarn("Sidebar menu Polling Error", {
                            component: "SideBarMenu",
                            problemId: errorCode,
                            errorCode,
                            rawError: error,
                            errorMessage: `${errorCode} - Error occurred during polling for Loan File Sequence change.`
                        });
                        regenLogOnceRef.current = true;
                    }
                }
            }
        };

        const interval = setInterval(fetchLoanFileSequence, 30000);

        return () => {
            pollingRef.current = false;
            clearInterval(interval);
        };
    }, [loanFileSequence]);

    useEffect(() => {
        if (accessToken && loanID) {
            fetchDocumentsAndAttachments(accessToken, loanID)
                .then(({ documents, attachments }) => {
                    setDocuments(documents || []);
                    setAttachments(attachments || []);
                })
                .catch(err => console.error("Error fetching documents:", err));
        }
    }, [accessToken, loanID]);

    const getLoanConditions = async (loanid) => {
        const tpoAuthObject = await window.elli.script.getObject("auth");
        const authCode = await tpoAuthObject.createAuthCode();
        const token = await getAccessToken(authCode);
        if (token) {
            var value = await fetchLoanConditions(token, loanid);
            const all = Array.isArray(value) ? value : [];
            setAllConditions(all);
            // filter only "true" status
            const filtered = all.filter(c => c.statusOpen === true);
            setConditions(filtered);
        }
    };

    const loadData = async (loanGuid, loanNumber = currentLoanNumber) => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setAccessToken(token);
            const allFieldIds = Array.from(new Set([
                ...loanFieldIdsToRead,
                ...menuItemsCP.concat(menuItemsBP).flatMap(item => [
                    item.fieldId,
                    item.requiredFieldId
                ]).filter(Boolean)
            ]));

            const fieldResponse = await fieldReader(token, allFieldIds, loanGuid);

            // get tpo organiztion details
            setLoanFileSequence(fieldResponse["LOANFILESEQUENCENUMBER"]);
            const tpoId = fieldResponse["TPO.X15"];
            setLoanSubmit(fieldResponse["TPO.X90"]);
            if (!tpoId) {
                setShowRequestLE(true);
                setShowSendDisclosures(true);
            } else {
                const tpoAuthCodes = await tpoAuthObject.createAuthCode();
                const tpoToken = await getAccessToken(tpoAuthCodes);
                const tpoOrganization = await getTpoOrganizationById(tpoToken, tpoId);

                if (!tpoOrganization || tpoOrganization.notFound) {
                    setShowRequestLE(true);
                    setShowSendDisclosures(true);
                } else {
                    const brokerOptions = tpoOrganization.brokerOptions;
                    setbrokerOptions(tpoOrganization.brokerOptions);

                    switch (brokerOptions?.toLowerCase()) {
                        case 'generateleanddisclosures':
                            setShowSendDisclosures(true);
                            setShowRequestLE(true);
                            break;
                        case 'requestleanddisclosures':
                            setShowDisclosures(true);
                            setShowRequestLE(true);
                            break;
                        default:
                            break;
                    }
                }
            }



            // ----------------------------------------------------
            // Disclosures logic:
            // - New loans: CX.ALL.DISCLOSURES.SENT = "Y" means disclosed
            // - Old loans: CX.ALL.DISCLOSURES.SENT may be blank, so if TPO.X90 has a real submit date,
            //   treat disclosures as already "viewable" (rename to View Disclosures + mark completed)
            // ----------------------------------------------------
            const isEmptyDateLike = (v) => {
                const s = (v ?? "").toString().trim();
                return s === "" || s === "//" || s === "  /  /    ";
            };

            const cxAll = (fieldResponse["CX.ALL.DISCLOSURES.SENT"] ?? "").toString().trim().toUpperCase();
            const x90 = fieldResponse["TPO.X90"];

            const allDisclosuresSent = cxAll === "Y";
            const submittedAlready = !isEmptyDateLike(x90);

            const showViewDisclosures = allDisclosuresSent || (cxAll === "" && submittedAlready);

            fieldResponse["HAS_ALL_BORROWERS_DISCLOSED"] = showViewDisclosures;

            setLoanTypeValue(fieldResponse["CX.LOAN.TYPE"]);
            setLoanOccupancy(fieldResponse["1811"]);
            setLoanOfficerNMLSID(fieldResponse["3238"]);
            setLoanFields(fieldResponse);

            await fetchAiDocReviewPendingCount(loanNumber);
            // Initialize count visibility for all menu items
            const visibility = {};
            [...menuItemsCP, ...menuItemsBP].forEach(item => {
                if (item.showCount) {
                    visibility[item.label] = true;
                }
            });
            setCountVisibility(visibility);

        } catch (error) {
            const errorCode = logError("SIDEBAR_LOAD_ERROR");
            logAppInsightError("Side Bar Menu Initialization Failed", {
                component: "SideBarMenu",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load Side Bar Menu Loan Information.`
            });
            setErrorMessage("An error occurred while loading the loan data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!accessToken || !loanID || loading) return;

        let isCancelled = false;

        const loadServiceOrdersForMenu = async () => {
            try {
                const orders = await getServiceOrders(accessToken, loanID);

                if (!isCancelled) {
                    setServiceOrders(orders || []);
                }
            } catch (err) {
                console.error("[SidebarMenu] Failed to load service orders:", err);

                if (!isCancelled) {
                    setServiceOrders([]);
                }
            }
        };

        loadServiceOrdersForMenu();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, loanID, loading]);

    const isBusinessLoan = (loanTypeValue) =>
        loanTypeValue?.toLowerCase().includes("business");

    const getSelectedMenu = (loanOccupancy, loanTypeValue, loanFields) => {
        const occupancy = loanOccupancy?.trim();
        // Correspondent flow
        if (isCorrespondentLoan(loanFields)) {
            return isDelegateLoan(loanFields)
                ? occupancy === 'Investor' ? menuItemsDelegateBP : menuItemsDelegate
                : occupancy === 'Investor' ? menuItemsNonDelegateBP : menuItemsNonDelegate;
        }

        // Existing BP / CP logic (unchanged)

        if (occupancy) {
            return occupancy === 'Investor' ? menuItemsBP : menuItemsCP;
        }

        return isBusinessLoan(loanTypeValue) ? menuItemsBP : menuItemsCP;
    };


    const isCorrespondentLoan = (loanFields) => {
        return loanFields?.["2626"] === "Correspondent";
    };

    const isDelegateLoan = (loanFields) => {
        return loanFields?.["TPO.X88"] === "Y";
    };


    const menu = useMemo(() => {
        if (
            !loanFields ||
            Object.keys(loanFields).length === 0
        ) {
            return [];
        }

        let selectedMenu = getSelectedMenu(
            loanOccupancy,
            loanTypeValue,
            loanFields
        );

        // Apply NMLS override if NMLS ID exists
        if (isBusinessLoan(loanTypeValue) && loanOfficerNMLSID?.trim()) {
            const creditIndex = selectedMenu.findIndex(item => item.label === 'CorelogicCredco');
            if (creditIndex !== -1) {
                selectedMenu[creditIndex] = {
                    label: 'DUAL AUS',
                    displayName: 'Order/ Reissue Credit',
                    icon: CreditCard,
                    isMenuCompleted: isCreditMenuCompleted
                };
            }
        }

        // Pass showDisclosures as extraContext to evaluateMenuCondition
        const extraContext = {
            showDisclosures,
            showRequestLE,
            showSendDisclosures,
            brokerOptions,
            serviceOrders
        };

        // Permission-based hiding applies only to the correspondent channel.
        const applyPermissionGate = isCorrespondentLoan(loanFields);

        return selectedMenu.map(item => {
            // Process subsections if they exist
            if (item.subsections) {
                return {
                    ...item,
                    subsections: item.subsections.filter(subItem => {
                        if (subItem.showIf && !evaluateMenuCondition(subItem.showIf, loanFields, extraContext)) {
                            return false;
                        }
                        if (applyPermissionGate && subItem.requiredPermission &&
                            !hasPermission(userPersonas, subItem.requiredPermission)) {
                            return false;
                        }
                        return true;
                    })
                };
            }
            return item;
        }).filter(item => {
            if (item.showIf && !evaluateMenuCondition(item.showIf, loanFields, extraContext)) {
                return false;
            }
            if (applyPermissionGate && item.requiredPermission &&
                !hasPermission(userPersonas, item.requiredPermission)) {
                return false;
            }
            return true;
        });
    }, [loanFields, loanTypeValue, loanOccupancy, showDisclosures, showRequestLE, serviceOrders,]);


    const toggleSection = (section) => {
        setExpandedSections(prev => {
            const isAlreadyOpen = prev[section];
            const newState = {};
            if (!isAlreadyOpen) {
                newState[section] = true; // Only open the clicked section
            }
            return newState; // Close all others
        });
    };


    const redirectToLoanPage = async (pageName, displayName, extPageId = null, mainMenu) => {
        const DIRTY_FLAG_KEY = "loan_additional_info_dirty";
        const channel = new BroadcastChannel('tpo_dirty_check_channel'); //shared cross-iframe channel

        try {
            const section = pageName.startsWith("URLA:") ? pageName.split(":")[1].trim() : null;
            const redirectPage = pageName;

            // Hide count if this menu item has hideOnNavigate configured
            setCountVisibility(prev => {
                const menuItem = [...menuItemsCP, ...menuItemsBP].find(item => item.label === pageName);
                if (menuItem?.showCount?.hideOnNavigate) {
                    return { ...prev, [pageName]: false };
                }
                return prev;
            });

            if (!window.elli?.script) {
                console.warn("elli.script is not available for redirection.");
                return;
            }

            const tpoAppObject = await window.elli.script.getObject("tpoApplication");

            // --- Cross-iframe Dirty Check ---
            const isAdditionalInfoDirty = localStorage.getItem(DIRTY_FLAG_KEY) === "true";

            if (isAdditionalInfoDirty) {
                // Ask AdditionalInfoPage to confirm navigation
                channel.postMessage({ type: 'TRIGGER_DIRTY_CHECK' });

                // Wait for reply from AdditionalInfoPage
                const approved = await new Promise((resolve) => {
                    const handler = (event) => {
                        if (event.data?.type === 'DIRTY_CHECK_RESULT') {
                            channel.removeEventListener('message', handler);
                            resolve(event.data.approved);
                        }
                    };
                    channel.addEventListener('message', handler);
                });

                if (!approved) {
                    console.log("Navigation cancelled by user in AdditionalInfoPage.");
                    return; // stop redirect
                }

                console.log("Navigation approved by user in AdditionalInfoPage.");
            }

            // Validation Handling
            const isValidationEnabled = process.env.REACT_APP_ENABLE_PAGE_VALIDATION === "true";
            if (isValidationEnabled) {
                const selectedMenu = loanTypeValue.includes('Business') ? menuItemsBP : menuItemsCP;

                let menuItem = null;
                if (displayName === "Order Appraisal" || displayName === "Order Smart Fees") {
                    menuItem = selectedMenu.find(item => item.displayName === displayName);
                } else {
                    menuItem = selectedMenu.find(item => item.label === pageName);
                }

                // Business + NMLS ID swaps CorelogicCredco for DUAL AUS, which only exists in the CP menu
                if (pageName === "DUAL AUS") {
                    menuItem = menuItemsCP.find(item => item.label === pageName);
                }

                if (!mainMenu) {
                    for (const item of selectedMenu) {
                        if (item.subsections) {
                            const subItem = item.subsections.find(sub => sub.label === pageName);
                            if (subItem) {
                                menuItem = subItem;
                                break;
                            }
                        }
                    }
                }

                if (menuItem?.validationExtPageId && menuItem?.validationPage) {
                    const needsValidation = await checkValidationRequired(menuItem, loanID, { brokerOptions }, accessToken);
                    if (needsValidation) {
                        if (menuItem.validationPage === "GuidelineExceptionValidation") {
                            markGuidelineExceptionValidationRedirect();
                        }

                        await tpoAppObject.pageRedirect("ExternalLoanPage", {
                            id: loanID,
                            extpageid: menuItem.validationExtPageId,
                            pagename: menuItem.validationPage
                        });
                        return;
                    }
                }
            }

            if (extPageId) {
                await tpoAppObject.pageRedirect("ExternalLoanPage", {
                    id: loanID,
                    pagename: redirectPage,
                    extpageid: extPageId,
                });
            } else {
                await tpoAppObject.pageRedirect(section ? "urla" : redirectPage, {
                    id: loanID,
                    ...(section && { section }),
                });
            }
        } catch (err) {
            const errorCode = logError("SIDEBAR_REDIRECT_ERROR");
            console.error("Redirect failed:", err);
            logAppInsightError("Loan Page Redirect Error", {
                component: "SidebarMenu",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Loan Page Redirect failed.`,
            });
        }
    };


    // Helper to render count badge if configured
    const renderCountBadge = (item) => {
        if (!item.showCount || !countVisibility[item.label]) return null;

        const count = item.label.toLowerCase() == "enhanced conditions" || "condition beta" ? conditionsCount.length : loanFields[item.showCount.fieldId] || 0;
        if (count <= 0) return null;

        return (
            <span className="brand-sidebar-count ml-2 text-xs font-semibold rounded-full px-2 py-0.5">
                {count}
            </span>
        );
    };


    const isMenuCompleted = (item, loanFields, extraContext = {}) => {
        if (!item || !loanFields) return false;
        const normalizedLabel = (item?.label || item?.displayName || "").toString().toLowerCase();

        if (normalizedLabel === "upload conditions" && isConditionBetaEnabled) {
            const allConditionsForCompletion = Array.isArray(extraContext?.allConditions)
                ? extraContext.allConditions
                : [];

            return allConditionsForCompletion.every((condition) => {
                if (!Array.isArray(condition?.assignedTo) || condition.assignedTo.length === 0) {
                    return false;
                }

                return condition.assignedTo.some((assignment) => {
                    if (!assignment || typeof assignment !== "object") return false;

                    return Object.values(assignment).some((value) => {
                        if (value === null || value === undefined) return false;
                        if (typeof value === "string") return value.trim() !== "";
                        return true;
                    });
                });
            });
        }

        if (normalizedLabel === "documents" || normalizedLabel === "efolder" || normalizedLabel === "documents beta") {
            let requiredFolders = [];
            // Income Type–based
            const incomeType = loanFields["CX.INCOME.TYPE"];
            const loanProduct = loanFields["1401"];
            if (loanFields["2626"]?.toLowerCase() === "correspondent") {
                correspondentDocumentMenuCompleteRules.forEach(rule => {
                    if (rule.condition(loanFields)) {
                        requiredFolders.push(...rule.folders);
                    }
                });

                // Validate all required folders
                if (requiredFolders.length === 0) return false;
                return checkFoldersComplete(requiredFolders, documents, attachments);
            }

            // if income type missing, treat as incomplete
            if (!loanProduct || loanProduct.trim() === "") {
                return false;
            }

            if (!incomeType || incomeType.trim() === "") {
                return false;
            }
            if (incomeType && incomeTypeToFolderMap[incomeType]) {
                requiredFolders.push(...incomeTypeToFolderMap[incomeType]);
            }

            // Condition-based
            conditionToFolderRules.forEach(rule => {
                if (rule.condition(loanFields)) {
                    requiredFolders.push(...rule.folders);
                }
            });

            // Validate all required folders
            if (requiredFolders.length === 0) return false;
            return checkFoldersComplete(requiredFolders, documents, attachments);
        }

        if (item.isMenuCompleted && Array.isArray(item.isMenuCompleted)) {
            return item.isMenuCompleted.every(id => {
                const val = loanFields[id];
                return val !== null && val !== undefined && val !== '' && val !== '//';
            });
        }

        if (typeof item.isMenuCompleted === "function") {
            return item.isMenuCompleted(loanFields, extraContext);
        }

        if (item.label == 'Service Orders') {
            return null;
        }

        if (item.label == 'LoanComment') {
            const count = loanFields[item.showCount.fieldId] || 0;
            if (count <= 0) return null;
        }

        // Default to false if nothing is configured
        return false;
    };

    const checkFoldersComplete = (requiredFolders, documents, attachments) => {
        return requiredFolders.every(folderName => {
            const doc = documents.find(
                d => d.title?.toLowerCase().trim() === folderName.toLowerCase().trim()
            );
            if (!doc) return false;

            const hasAttachment = attachments.some(
                att => att?.assignedTo?.entityId === doc.id
            );
            return !!hasAttachment;
        });
    };


    const fetchDocumentsAndAttachments = async (token, loanId) => {
        const docsUrl = `/encompass/v3/loans/${loanId}/documents?includeRemoved=true&requireActiveAttachments=false`;
        const attsUrl = `/encompass/v3/loans/${loanId}/attachments?includeRemoved=true`;

        const [documents, attachments] = await Promise.all([
            elliemaeApi.get(docsUrl, { headers: { Authorization: `Bearer ${token}` } }).then((response) => response.data),
            elliemaeApi.get(attsUrl, { headers: { Authorization: `Bearer ${token}` } }).then((response) => response.data)
        ]);

        return { documents, attachments };
    };



    if (loading) {
        return <SidebarMenuFacebookLoader />;
    }

    if (errorMessage) {
        return (
            <div className="p-4 text-red-600 bg-red-100 rounded-md">
                {errorMessage}
            </div>
        );
    }

    let stepCounter = 1;

    const menuExtraContext = {
        showDisclosures,
        showRequestLE,
        showSendDisclosures,
        brokerOptions,
        serviceOrders,
        aiDocReviewPendingCount,
        aiDocReviewStatusLoaded,
        openConditions: conditionsCount,
        allConditions
    };

    // Steps (only non-excluded items)
    const stepItems = menu.filter(item => !item.isExcludeFromSequence);

    // Completed steps
    const completedSteps = stepItems.filter(item => {
        const displayName = typeof item.displayName === 'function'
            ? item.displayName(loanFields)
            : item.displayName;

        return (
            displayName === "Loan Summary" ||
            //displayName === "View Disclosures" ||
            isMenuCompleted(item, loanFields, menuExtraContext)
            //||(displayName === "Conditions" && conditions.length <= 0)
        );
    }).length;

    const totalSteps = stepItems.length;
    const remainingSteps = totalSteps - completedSteps;

    let separatorRendered = false; // track if we already rendered the separator

    return (
        <div className="brand-sidebar w-80 min-h-screen">

            {/* Steps counter */}
            {(!loanSubmit || loanSubmit === '//' || loanSubmit === '  /  /    ') && (
                <div className="brand-sidebar-header flex items-center justify-between px-2 py-1 text-lg font-semibold mb-2">
                    Submission Steps
                </div>
            )}
            {/* Single map for all menus */}
            {menu.map((item, index) => {
                const displayName = typeof item.displayName === "function"
                    ? item.displayName(loanFields)
                    : item.displayName;

                const isExcluded = item.isExcludeFromSequence;
                const isCompleted =
                    displayName === "Loan Summary" ||
                    //displayName === "View Disclosures" ||
                    isMenuCompleted(item, loanFields, menuExtraContext);
                //|| (displayName === "Conditions" && conditions.length <= 0);

                // Insert separator before the first excluded item
                if (isExcluded && !separatorRendered) {
                    separatorRendered = true;
                    return (
                        <React.Fragment key={`separator-${index}`}>
                            <div className="brand-sidebar-divider my-2"></div>
                            {renderExcludedMenu(item, displayName, isCompleted, index)}
                        </React.Fragment>
                    );
                }

                return isExcluded
                    ? renderExcludedMenu(item, displayName, isCompleted, index)
                    : renderStepMenu(item, displayName, isCompleted, stepCounter++, index);
            })}
        </div>
    );

    // --- Helper render functions ---
    function renderStepMenu(item, displayName, isCompleted, stepNumber, index) {
        return (
            <div key={index}>
                <div
                    onClick={() => {
                        if (item.subsections) {
                            toggleSection(displayName);
                            setActiveMenu(displayName);
                        } else {
                            setActiveMenu(displayName);
                            redirectToLoanPage(item.label, displayName, item.extPageId, true);
                        }
                    }}
                    className={`brand-sidebar-item flex items-center justify-between p-2 mb-1 rounded cursor-pointer
                    ${activeMenu === displayName ? "is-active" : ""}
                    ${item.subsections ? "pr-2" : ""}`}
                >
                    {/* Step number or tick */}
                    <div className="flex items-center">
                        {isCompleted ? (
                            <div className="w-7 h-7 brand-sidebar-step brand-sidebar-step--done text-sm mr-3">
                                <Check size={20} className="stroke-current" strokeWidth={3} />
                            </div>
                        ) : (
                            <div className="w-6 h-6 brand-sidebar-step text-sm mr-3">
                                {stepNumber}
                            </div>
                        )}
                        <span className="flex items-center space-x-2">
                            <span>{displayName}</span>
                            {renderCountBadge(item)}
                        </span>
                    </div>
                    {/* Chevron if expandable */}
                    {item.subsections && (
                        expandedSections[item.displayName] ? (
                            <ChevronDown className="w-10 h-4 ml-6" />
                        ) : (
                            <ChevronRight className="w-10 h-4 ml-6" />
                        )
                    )}
                </div>

                {/* Subsections */}
                {item.subsections && expandedSections[item.displayName] && (
                    <div className="brand-sidebar-submenu">
                        {item.subsections.map((subItem, subIndex) => (
                            <div
                                key={subIndex}
                                onClick={() => {
                                    setActiveMenu(subItem.displayName);
                                    redirectToLoanPage(subItem.label, subItem.displayName, subItem.extPageId, false);
                                }}
                                className={`brand-sidebar-subitem flex items-center justify-between p-1 pl-4 mb-1 rounded cursor-pointer
                                ${activeMenu === subItem.displayName ? "is-active" : ""}`}
                            >
                                <span className="text-sm">{subItem.displayName}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    function renderExcludedMenu(item, displayName, isCompleted, index) {
        return (
            <div key={index}>
                <div
                    onClick={() => {
                        if (item.subsections) {
                            toggleSection(displayName);
                            setActiveMenu(displayName);
                        } else {
                            setActiveMenu(displayName);
                            redirectToLoanPage(item.label, displayName, item.extPageId, true);
                        }
                    }}
                    className={`brand-sidebar-item flex items-center justify-between p-2 mb-1 rounded cursor-pointer
                    ${activeMenu === displayName ? "is-active" : ""}
                    ${item.subsections ? "pr-2" : ""}`}
                >
                    {/* Icon OR Tick (not both) */}
                    <div className="flex items-center">
                        {isCompleted ? (
                            <div className="w-7 h-7 brand-sidebar-step brand-sidebar-step--done text-xs mr-3">
                                <Check size={20} className="stroke-current" strokeWidth={3} />
                            </div>
                        ) : (
                            <item.icon className="brand-sidebar-icon mr-3 w-6 h-6" />
                        )}
                        <span className="flex items-center space-x-2">
                            <span>{displayName}</span>
                            {renderCountBadge(item)}
                        </span>
                    </div>

                    {/* Chevron if expandable */}
                    {item.subsections && (
                        expandedSections[item.displayName] ? (
                            <ChevronDown className="w-10 h-4 ml-6" />
                        ) : (
                            <ChevronRight className="w-10 h-4 ml-6" />
                        )
                    )}
                </div>

                {item.subsections && expandedSections[item.displayName] && (
                    <div className="brand-sidebar-submenu">
                        {item.subsections.map((subItem, subIndex) => (
                            <div
                                key={subIndex}
                                onClick={() => {
                                    setActiveMenu(subItem.displayName);
                                    redirectToLoanPage(subItem.label, subItem.displayName, subItem.extPageId, false);
                                }}
                                className={`brand-sidebar-subitem flex items-center justify-between p-1 pl-4 mb-1 rounded cursor-pointer
                                ${activeMenu === subItem.displayName ? "is-active" : ""}`}
                            >
                                <span className="text-sm">{subItem.displayName}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }


};

export default SidebarMenu;
